import React from "react";

export interface SwitchProps {
  /** Estado atual. Controlado: quem chama guarda o valor e decide quando ele muda. */
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  /** Nome acessível do controle. Só ele entra no nome; a descrição fica de fora. */
  label: React.ReactNode;
  /** Explicação curta abaixo do rótulo, associada por `aria-describedby`. */
  description?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  /**
   * Por que o controle está desligado agora, em uma frase.
   *
   * Muda o modo de desabilitar: com um motivo, o interruptor sai do atributo
   * `disabled` e fica só com `aria-disabled`. Ele continua alcançável pelo Tab
   * e o motivo é anunciado ao receber foco. Sem isso, quem navega por teclado
   * passa direto por um controle que não funciona e nunca descobre por quê —
   * `disabled` tira o controle da ordem de tabulação e leva a explicação junto.
   * O clique continua inerte nos dois modos.
   */
  disabledReason?: string;
  /** Gravação em curso: trava o controle e anuncia `aria-busy`. */
  loading?: boolean;
  className?: string;
  /**
   * `card` (padrão) é a linha com borda própria, e ligada ganha fundo violeta.
   * `row` é para quando vários interruptores vivem numa mesma lista com
   * divisórias: sem borda nem fundo próprios, para não virar card dentro de
   * card (DESIGN.md, "Regra da Linha, Não da Caixa"). Ligado aparece só no
   * trilho do interruptor, e não pintando a linha inteira.
   */
  variant?: "card" | "row";
}

/**
 * Interruptor do design system.
 *
 * A linha inteira é o alvo de toque — rótulo, descrição e trilho dentro do mesmo
 * `<button>` — porque um trilho de 24px ao lado de um texto clicável é o tipo de
 * alvo que só funciona no mouse. Daí o `min-h-[44px]`: `min-h-11` não emite
 * regra no Twind deste projeto (`docs/DESIGN_SYSTEM.md` §3).
 *
 * O estado desligado/ligado nunca é comunicado só pela cor: `role="switch"` +
 * `aria-checked` dizem o mesmo para quem não enxerga o trilho.
 *
 * Quando o motivo de estar travado importa, quem chama passa `disabledReason`:
 * o controle troca `disabled` por `aria-disabled` e continua no Tab, para que a
 * explicação chegue a quem navega por teclado. Ver a prop.
 */
export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  icon,
  disabled = false,
  disabledReason,
  loading = false,
  className = "",
  variant = "card",
}: SwitchProps) {
  const linha = variant === "row";
  const labelId = React.useId();
  const descriptionId = React.useId();
  const reasonId = React.useId();
  const blocked = disabled || loading;
  // `loading` fica de fora de propósito: é estado de um instante, e o
  // `aria-busy` já conta o que está acontecendo.
  const explicado = disabled && !loading && Boolean(disabledReason);
  const describedBy =
    [description ? descriptionId : null, explicado ? reasonId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      aria-describedby={describedBy}
      aria-disabled={blocked || undefined}
      aria-busy={loading || undefined}
      // Gravando (`loading`), o botão segue focável e só avisa por
      // `aria-disabled`: o `disabled` nativo tirava o foco a cada troca e quem
      // usa teclado voltava para o começo da página.
      disabled={disabled && !loading && !explicado}
      onClick={() => {
        if (blocked) return;
        onCheckedChange(!checked);
      }}
      className={`flex min-h-[44px] w-full items-center justify-between gap-4 text-left transition-colors duration-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 ${
        explicado ? "cursor-not-allowed opacity-60" : ""
      } ${
        linha
          ? "px-4 py-4 text-slate-900 hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-primary-100 sm:px-5"
          : `rounded-2xl border p-3 focus-visible:ring-4 focus-visible:ring-primary-100 ${
              checked
                ? "border-primary-200 bg-primary-50 text-primary-700"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`
      } ${className}`}
    >
      <span className="min-w-0">
        <span id={labelId} className="flex items-center gap-2 text-sm font-semibold">
          {icon ? (
            <span
              className={`inline-flex shrink-0 ${linha ? "text-slate-400" : ""}`}
              aria-hidden="true"
            >
              {icon}
            </span>
          ) : null}
          <span className="min-w-0">{label}</span>
        </span>
        {description ? (
          <span
            id={descriptionId}
            className={`mt-1 block leading-5 text-slate-500 ${
              linha ? "text-sm" : "text-xs font-medium"
            }`}
          >
            {description}
          </span>
        ) : null}
        {explicado ? (
          // Só para leitor de tela: o motivo já está visível no aviso do card,
          // e repeti-lo em cada linha encheria a tela de texto igual.
          <span id={reasonId} className="sr-only">
            {disabledReason}
          </span>
        ) : null}
      </span>
      <span
        className={`flex h-6 w-11 shrink-0 items-center rounded-full p-1 transition-colors duration-200 ${
          checked ? "bg-primary-600" : "bg-slate-200"
        }`}
        aria-hidden="true"
      >
        {loading ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
        ) : (
          <span
            className={`h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
              checked ? "translate-x-5" : "translate-x-0"
            }`}
          />
        )}
      </span>
    </button>
  );
}
